import { fmtDate } from './formatters';

/**
 * O MARCADOR do último contato com o órgão na tela de Reajustes (espelho da tratativa, §10.2 do contrato do backend;
 * #579). O servidor decide o que é "último contato" e "sem resposta", pela ordem (occurredOn, id): resposta no mesmo
 * dia conta, mas resposta seguida de envio nosso no mesmo dia não conta (o IPREJUN de 21/09). A tela só decide o
 * VERMELHO, e o limite dele mora aqui para trocar sem deploy do backend.
 *
 * ⛔ `null` quer dizer "ninguém registrou contato" (133 dos 150 contratos em 30/10/2026), e não "contato recente". A
 * frase diz isso com todas as letras, e nada aqui é verde: uma linha que ninguém tocou não pode parecer em dia.
 */

export interface UltimoContato {
  /** O canal do evento que venceu; `null` quando a data veio da coluna digitada no contrato ("informação pedida em"). */
  canal: string | null;
  /** Dia civil, YYYY-MM-DD. */
  em: string;
  direcao: 'enviado' | 'recebido' | 'interno';
  haDias: number;
  semRespostaDoOrgao: boolean;
  diasSemResposta: number | null;
}

/** Mais que isto sem resposta do órgão, a linha fica vermelha. Decisão do Heitor (30 dias), na tela de propósito. */
export const LIMITE_SEM_RESPOSTA_DIAS = 30;

/** Os oito canais do `CreateAdjustmentEventDto`, como a tela os nomeia. */
export const CANAIS_DO_EVENTO = [
  { value: 'email', label: 'e-mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telefone', label: 'telefone' },
  { value: 'oficio', label: 'ofício' },
  { value: 'processo', label: 'processo' },
  { value: 'reuniao', label: 'reunião' },
  { value: 'sistema', label: 'sistema' },
  { value: 'outro', label: 'outro' },
] as const;

export const rotuloDoCanal = (canal: string | null | undefined): string =>
  canal ? (CANAIS_DO_EVENTO.find((c) => c.value === canal)?.label || canal) : 'pedido anotado no contrato';

const haQuanto = (n: number) => (n <= 0 ? 'hoje' : `há ${n} ${n === 1 ? 'dia' : 'dias'}`);

/** O que a linha diz, e se fica vermelha. */
export function marcadorDoUltimoContato(uc: UltimoContato | null | undefined): { texto: string; vermelho: boolean; semContato: boolean } {
  if (!uc) return { texto: 'nenhum contato registrado', vermelho: false, semContato: true };
  const dia = fmtDate(uc.em).slice(0, 5);
  const base = `último contato: ${rotuloDoCanal(uc.canal)} · ${dia} · ${haQuanto(Number(uc.haDias) || 0)}`;
  if (uc.semRespostaDoOrgao) {
    const dias = Number(uc.diasSemResposta);
    return {
      texto: `${base}, sem resposta${Number.isFinite(dias) && dias !== Number(uc.haDias) ? ` há ${dias} dias` : ''}`,
      vermelho: Number.isFinite(dias) && dias > LIMITE_SEM_RESPOSTA_DIAS,
      semContato: false,
    };
  }
  return { texto: uc.direcao === 'recebido' ? `${base}, eles responderam` : base, vermelho: false, semContato: false };
}

export type DirecaoDoRegistro = 'enviado' | 'recebido';

export interface RegistroDeContato {
  canal: string;
  /** Obrigatória (S1): a tela não manda sem ela. `enviado` = falei com eles; `recebido` = eles responderam. */
  direcao: DirecaoDoRegistro | null;
  texto: string;
  /** YYYY-MM-DD, sem hora: o DTO recusa hora. */
  data: string;
  quem?: string;
}

/** Por que ainda não dá para registrar, ou `null`. As mesmas regras do DTO, para o botão não oferecer o que dá 400. */
export function problemaDoRegistro(r: RegistroDeContato): string | null {
  if (!CANAIS_DO_EVENTO.some((c) => c.value === r.canal)) return 'Escolha o canal.';
  if (r.direcao !== 'enviado' && r.direcao !== 'recebido') return 'Diga se foi a casa que falou ou se o órgão respondeu.';
  const t = r.texto.trim().length;
  if (t < 5) return 'Escreva o que aconteceu, com pelo menos 5 letras.';
  if (t > 500) return 'O texto passa de 500 letras.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.data)) return 'Informe a data.';
  return null;
}

/**
 * O corpo do POST de evento. ⛔ `direction` sempre EXPLÍCITO: o padrão do backend é "recebido", e um contato da casa sem
 * ele entraria como se o órgão tivesse falado, e apagaria o vermelho de quem não respondeu.
 */
export function corpoDoRegistro(r: RegistroDeContato) {
  const quem = (r.quem || '').trim().slice(0, 200);
  return {
    occurredOn: r.data,
    channel: r.canal,
    direction: r.direcao as DirecaoDoRegistro,
    summary: r.texto.trim(),
    ...(quem ? { counterpart: quem } : {}),
  };
}
